<script setup>
import { ref, computed, watch } from 'vue';
import FileMenu from './FileMenu.vue';
import AddPanel from '../AddPanel.vue';
import { useFileActions } from '../../composables/useFileActions.js';
import { useAuth } from '../../composables/useAuth.js';
import { fmtDate, sortByDate } from '../../domain/format.js';
import DateField from '../DateField.vue';

/* Vereinsverträge. Zugriff über den Bereich "contracts" (admin, board, data).
   Die Einträge stehen seit Stufe 3 (Teil 2) in der Tabelle
   player_contract_files und hängen als `player.contractFiles` im Speicher
   (domain/contracts.js). Neue Einträge sind mit `_new` markiert, entfernte
   landen in `removedContractFiles`; beides wird erst mit dem Speichern-Knopf
   des Panels in die Datenbank geschrieben (und erst dann die Datei gelöscht).
   Ohne `contractFiles` wurde nicht geladen — dann kein Bearbeiten. */
const props = defineProps({
  player: { type: Object, required: true },
});

const { can } = useAuth();
const { upload, busy } = useFileActions();

const fileInput = ref(null);
const newTitle = ref('');
const newDate = ref('');
/* Dezenter Hinweis, wenn ohne Titel hochgeladen wird — blockiert nichts,
   macht nur sichtbar, dass sonst der Dateiname als Anzeige benutzt wird. */
const showTitleHint = ref(false);

watch(newTitle, (v) => {
  if (v.trim()) showTitleHint.value = false;
});

/* Ein neu angelegter Spieler hat noch keine id — der Ablagepfad und der
   Fremdschlüssel brauchen sie. Erst speichern, dann Verträge hinzufügen. */
const canEdit = computed(
  () => can('contracts', 'write') && !!props.player.contractFiles && !props.player._isNew
);

const entries = computed(() =>
  sortByDate(props.player.contractFiles || [], 'signedDate').map((c) => ({
    contract: c,
    idx: props.player.contractFiles.indexOf(c),
  }))
);

async function onPick(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  const stored = await upload(file, `clubContracts/${props.player.id}`);
  props.player.contractFiles.push({
    id: crypto.randomUUID(),
    _new: true,
    ...stored,
    label: newTitle.value.trim() || file.name,
    signedDate: newDate.value,
    note: '',
  });
  newTitle.value = '';
  newDate.value = '';
}

function promptUpload() {
  showTitleHint.value = !newTitle.value.trim();
  fileInput.value.click();
}

function removeContract(idx) {
  const [gone] = props.player.contractFiles.splice(idx, 1);
  if (!gone) return;
  if (!props.player.removedContractFiles) props.player.removedContractFiles = [];
  props.player.removedContractFiles.push(gone);
}
</script>

<template>
  <div class="doc-group">
    <h3>{{ $t('documents.contracts') }}</h3>
    <div class="hint hint-spaced">{{ $t('documents.contractsHint') }}</div>

    <div v-if="!entries.length" class="feed-empty">{{ $t('documents.noContracts') }}</div>

    <div v-for="{ contract, idx } in entries" :key="idx" class="uploaded-doc stacked">
      <div class="contract-view">
        <span class="ud-icon"><i>▪</i></span>
        <div class="ud-body">
          <div class="ud-label">{{ contract.label || $t('files.untitled') }}</div>
          <div v-if="contract.signedDate" class="ud-sub">
            {{ $t('documents.signedOn') }}: {{ fmtDate(contract.signedDate) }}
          </div>
          <div v-if="contract.note" class="ud-note">{{ contract.note }}</div>
          <FileMenu :file="contract" :name="contract.label" area="contracts" @remove="removeContract(idx)" />
        </div>
      </div>
    </div>

    <div v-if="canEdit" class="add-form stacked">
      <input
        v-model="newTitle"
        type="text"
        :class="{ 'title-empty': showTitleHint }"
        :placeholder="$t('documents.contractPlaceholder')"
      />
      <div v-if="showTitleHint" class="title-hint">{{ $t('files.titleMissingHint') }}</div>
      <div class="af-row">
        <label class="dated">
          <span>{{ $t('documents.signedOn') }}</span>
          <DateField v-model="newDate" />
        </label>
        <button class="file-label" :disabled="busy" @click="promptUpload">
          {{ busy ? $t('common.loading') : $t('files.uploadFile') }}
        </button>
      </div>
    </div>

    <input ref="fileInput" type="file" accept="application/pdf,image/*" hidden @change="onPick" />
  </div>
</template>

<style scoped>
.uploaded-doc.stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.contract-view {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.hint-spaced {
  margin-bottom: 10px;
}
.add-form.stacked {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.af-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: flex-end;
}
.dated {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: var(--fs-2xs);
  color: var(--dim);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.file-label {
  background: none;
  font: inherit;
}
.title-empty {
  border-color: var(--amber);
}
.title-hint {
  font-size: var(--fs-2xs);
  color: var(--amber);
}
</style>
